// Phase 9B — ApprovalQueue.
//
// Renders pending approval items with approve/reject actions.
// Human-in-the-loop control surface. Items requiring approval
// are queued here before going live.

import type { ApprovalWorkflow, UsePendingApprovalsState } from "../../boards/useBoardItems.ts";
import * as s from "./board.styles.ts";

// ─── Helpers ───────────────────────────────────────────────────

function fmtTs(raw: string): string {
  if (raw.length === 0) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const day = d.getUTCDate().toString().padStart(2, "0");
  const mon = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const hr = d.getUTCHours().toString().padStart(2, "0");
  const min = d.getUTCMinutes().toString().padStart(2, "0");
  return `${day} ${mon} ${hr}:${min}`;
}

function relativeTime(raw: string): string {
  const diff = Date.now() - new Date(raw).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${String(mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${String(hrs)}h ago`;
  const days = Math.floor(hrs / 24);
  return `${String(days)}d ago`;
}

const ACTION_LABELS: Record<string, string> = {
  go_live: "Go Live",
  increase_stake: "Increase Stake",
  accept_signal: "Accept Signal",
};

// ─── Component ─────────────────────────────────────────────────

export interface ApprovalQueueProps {
  state: UsePendingApprovalsState;
  onApprove?: (approvalId: string) => void;
  onReject?: (approvalId: string) => void;
}

export function ApprovalQueue({
  state,
  onApprove,
  onReject,
}: ApprovalQueueProps): JSX.Element | null {
  const { approvals, loading, error } = state;

  if (loading) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sz-space-3)" }}>
      <div style={s.boardHeader}>
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={s.boardTitle}>Approvals</span>
          <span style={s.boardSubtitle}>Human-in-the-loop control queue</span>
        </div>
        <span style={s.boardCount}>{String(approvals.length)} pending</span>
      </div>

      {error !== null ? <span style={s.emptyNote}>{error}</span> : null}

      {approvals.length === 0 && error === null ? (
        <div style={s.emptyState}>
          <span style={s.emptyTitle}>Queue Clear</span>
          <span style={s.emptyNote}>
            No items awaiting approval. Items requiring human review will appear here.
          </span>
        </div>
      ) : (
        <div style={s.boardList}>
          {approvals.map((aw) => (
            <div key={aw.id} style={s.approvalBanner}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={s.approvalText}>
                  <strong>{ACTION_LABELS[aw.requested_action] ?? aw.requested_action}</strong>
                  {" requested"}
                </span>
                <span style={{
                  fontFamily: "var(--sz-font-mono)",
                  fontSize: "10px",
                  color: "var(--sz-text-muted)",
                  letterSpacing: "1px",
                }}>
                  {relativeTime(aw.requested_at)} · {fmtTs(aw.requested_at)}
                </span>
              </div>
              <div style={{ display: "flex", gap: "var(--sz-space-1)" }}>
                {onApprove !== undefined ? (
                  <button
                    type="button"
                    style={s.approvalAction}
                    onClick={() => onApprove(aw.id)}
                  >
                    Approve
                  </button>
                ) : null}
                {onReject !== undefined ? (
                  <button
                    type="button"
                    style={s.approvalReject}
                    onClick={() => onReject(aw.id)}
                  >
                    Reject
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
